import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Item, ItemInsert, ItemUpdate } from '@/types/db';

export function useItems(dealId?: string) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadItems();
  }, [dealId]);

  async function loadItems() {
    try {
      setLoading(true);
      let query = supabase
        .from('items')
        .select('*, catalog(*)')
        .order('created_at', { ascending: false });

      if (dealId) {
        query = query.eq('deal_id', dealId);
      }

      const { data, error: err } = await query;

      if (err) throw err;
      setItems(data || []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }

  async function createItem(item: ItemInsert) {
    const { data, error: err } = await supabase
      .from('items')
      .insert(item)
      .select()
      .single();

    if (err) throw err;
    await loadItems();
    return data;
  }

  async function updateItem(id: string, updates: ItemUpdate) {
    const { data, error: err } = await supabase
      .from('items')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (err) throw err;
    await loadItems();
    return data;
  }

  async function deleteItem(id: string) {
    const { error: err } = await supabase
      .from('items')
      .delete()
      .eq('id', id);

    if (err) throw err;
    await loadItems();
  }

  return {
    items,
    loading,
    error,
    loadItems,
    createItem,
    updateItem,
    deleteItem,
  };
}
